/**
 * WebView Validation Utilities
 */

import { WebViewExternalProps, WebViewAuthConfig } from '../types';

/**
 * Validate WebView external props
 */
export function validateExternalProps(props: any): props is WebViewExternalProps {
  if (!props || typeof props !== 'object') {
    return false;
  }

  // Auth config is required
  if (!validateAuthConfig(props.authConfig)) {
    return false;
  }

  return true;
}

/**
 * Validate auth config
 */
export function validateAuthConfig(config: any): config is WebViewAuthConfig {
  if (!config || typeof config !== 'object') {
    return false;
  }

  // Required fields - made firebase URLs optional for WebView mode
  const requiredFields = ['userInfo', 'accessToken'];
  for (const field of requiredFields) {
    if (!config[field]) {
      console.error(`Missing required auth field: ${field}`);
      return false;
    }
  }
  
  // Firebase URLs are optional in WebView mode
  if (config.firebaseAuthUrl && !isValidUrl(config.firebaseAuthUrl)) {
    console.error('Invalid firebaseAuthUrl');
    return false;
  }
  if (config.firebaseAuthRootUrl && !isValidUrl(config.firebaseAuthRootUrl)) {
    console.error('Invalid firebaseAuthRootUrl');
    return false;
  }
  
  // TenantId can be empty string but must exist
  if (!('tenantId' in config)) {
    console.error('Missing tenantId field');
    return false;
  }

  // Validate user info
  if (!validateUserInfo(config.userInfo)) {
    return false;
  }

  // URLs validation is now done above

  return true;
}

/**
 * Validate user info
 */
function validateUserInfo(userInfo: any): boolean {
  if (!userInfo || typeof userInfo !== 'object') {
    return false;
  }

  const requiredFields = ['id', 'name', 'email'];
  for (const field of requiredFields) {
    if (!userInfo[field]) {
      console.error(`Missing required user info field: ${field}`);
      return false;
    }
  }

  // Handle both 'role' (string) and 'roles' (array) formats
  let userRole: string | undefined;
  if (userInfo.role) {
    userRole = userInfo.role;
  } else if (userInfo.roles && Array.isArray(userInfo.roles) && userInfo.roles.length > 0) {
    userRole = userInfo.roles[0];
  } else {
    console.error('Missing required user info field: role or roles');
    return false;
  }

  // Validate role
  const validRoles = ['student', 'teacher', 'admin', 'viewer', 'kullanici', 'ogrenci', 'ogretmen', 'yonetici'];
  if (!userRole || !validRoles.includes(userRole)) {
    console.error(`Invalid role: ${userRole}`);
    return false;
  }

  // Validate email format
  if (!isValidEmail(userInfo.email)) {
    console.error(`Invalid email format: ${userInfo.email}`);
    return false;
  }

  return true;
}

/**
 * Validate URL format
 */
function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate email format
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Sanitize external props
 */
export function sanitizeExternalProps(props: WebViewExternalProps): WebViewExternalProps {
  // Deep clone to avoid mutations
  const sanitized = JSON.parse(JSON.stringify(props));

  // Sanitize URLs
  if (sanitized.authConfig) {
    sanitized.authConfig.firebaseAuthUrl = sanitizeUrl(sanitized.authConfig.firebaseAuthUrl);
    sanitized.authConfig.firebaseAuthRootUrl = sanitizeUrl(sanitized.authConfig.firebaseAuthRootUrl);
  }

  // Sanitize user info
  if (sanitized.authConfig?.userInfo) {
    sanitized.authConfig.userInfo.name = sanitizeString(sanitized.authConfig.userInfo.name);
    sanitized.authConfig.userInfo.email = sanitizeString(sanitized.authConfig.userInfo.email);
  }

  return sanitized;
}

/**
 * Sanitize URL
 */
function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url);
    // Only allow HTTPS
    if (parsed.protocol !== 'https:') {
      parsed.protocol = 'https:';
    }
    return parsed.toString();
  } catch {
    return '';
  }
}

/**
 * Sanitize string
 */
function sanitizeString(str: string): string {
  return str.trim().replace(/[<>]/g, '');
}